import { useState, type ReactNode } from 'react';

import { IconButton, VecField } from '../../ui/controls.js';
import { CurveCanvas } from './CurveCanvas.js';
import { movePoint, project, tidyPoint, unproject, VIEWS, type PointEdit, type Vec3, type ViewName } from './curve-math.js';
import { CANVAS_HELP, CanvasTools } from './ProfileEditor.js';
import { usePointDraft } from './use-point-draft.js';
import './curves.css';

export interface PathEditorProps {
  points: readonly Vec3[];
  /** New points plus the structural edits, so parallel arrays can follow. */
  onChange: (points: Vec3[], edits: PointEdit[]) => void;
  closed?: boolean;
  smooth?: boolean;
  samples?: number;
  /** Circle drawn around each point (radius in m). */
  radii?: readonly (number | undefined)[];
  /** Non-editable 3D outlines (loft section rings, other hair guides). */
  ghosts?: readonly { points: readonly Vec3[]; closed?: boolean }[];
  /** Draw these point pairs instead of a curve (skeleton bones). */
  edges?: readonly (readonly [number, number])[];
  /** Clicking the curve inserts a point (off for skeletons). */
  insert?: boolean;
  /** Extra fields for the selected point (sweep scale, a skin node's radius...). */
  renderPoint?: (index: number) => ReactNode;
  /** Name shown for the selected point. */
  pointLabel?: (index: number) => string;
  height?: number;
}

const VIEW_NAMES: ViewName[] = ['top', 'front', 'side'];

/**
 * 3D point editor as three orthographic projections (top X·−Z, front X·Y,
 * side −Z·Y; one at a time or all three): every view edits the two
 * coordinates it shows and keeps the hidden one. Tube / sweep paths, loft
 * section positions, hair guides and skin skeletons.
 */
export function PathEditor({
  points,
  onChange,
  closed = false,
  smooth = false,
  samples = 6,
  radii,
  ghosts,
  edges,
  insert = true,
  renderPoint,
  pointLabel,
  height = 220,
}: PathEditorProps) {
  const [view, setView] = useState<ViewName | 'all'>('front');
  const [selected, setSelected] = useState<number | null>(null);
  const [snap, setSnap] = useState(false);
  const [grid, setGrid] = useState(0.05);
  const [frame, setFrame] = useState(0);
  const draft = usePointDraft<Vec3>(points, (next, edits) => onChange(next.map(tidyPoint), edits));
  const pts = draft.points;
  const sel = selected !== null && selected < pts.length ? selected : null;
  const views = view === 'all' ? VIEW_NAMES : [view];

  const canvas = (v: ViewName) => (
    <div key={v} className="path-view">
      {view === 'all' && <span className="path-view-label">{VIEWS[v].label}</span>}
      <CurveCanvas
        points={pts.map((p) => project(p, v))}
        closed={closed}
        smooth={smooth}
        samples={samples}
        selected={sel}
        onSelect={setSelected}
        onMove={(i, uv) => {
          const keep = draft.points[i];
          if (keep) draft.move(i, unproject(uv, v, keep));
        }}
        onCommit={draft.finish}
        onAdd={(uv) => {
          const keep = (sel !== null ? pts[sel] : pts[pts.length - 1]) ?? [0, 0, 0];
          return draft.add(unproject(uv, v, keep), undefined, sel);
        }}
        onInsert={
          insert
            ? (segment, uv, t) => {
                const a = pts[segment];
                const b = pts[(segment + 1) % pts.length];
                const keep: Vec3 = a && b ? [0, 1, 2].map((k) => a[k]! + (b[k]! - a[k]!) * t) as Vec3 : [0, 0, 0];
                return draft.add(unproject(uv, v, keep), segment + 1);
              }
            : undefined
        }
        onDelete={(i) => {
          draft.remove(i);
          setSelected(null);
        }}
        grid={grid}
        snap={snap}
        axes={VIEWS[v].axes}
        radii={radii}
        edges={edges}
        ghosts={ghosts?.map((g) => ({ points: g.points.map((p) => project(p, v)), closed: g.closed }))}
        height={view === 'all' ? Math.round(height * 0.7) : height}
        frameKey={`${v}${frame}`}
      />
    </div>
  );

  return (
    <div className="curve-editor">
      <CanvasTools snap={snap} grid={grid} onSnap={setSnap} onGrid={setGrid} onFrame={() => setFrame((f) => f + 1)}>
        <div className="seg-switch" role="tablist" aria-label="Projection">
          {[...VIEW_NAMES, 'all' as const].map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              className={view === v ? 'is-active' : ''}
              title={v === 'all' ? 'All three views' : `${VIEWS[v].label} view (${VIEWS[v].axes.join(' · ')})`}
              onClick={() => setView(v)}
            >
              {v === 'all' ? 'All' : VIEWS[v].label}
            </button>
          ))}
        </div>
        <span className="curve-count muted">{points.length} pts</span>
      </CanvasTools>
      {views.map(canvas)}
      <div className="curve-point-row">
        {sel !== null && pts[sel] ? (
          <>
            <span className="curve-point-index mono" title={pointLabel?.(sel)}>
              {pointLabel ? pointLabel(sel) : `#${sel}`}
            </span>
            <VecField step={0.01} value={pts[sel]} onChange={(p) => onChange(movePoint(points, sel, tidyPoint(p as Vec3)), [])} />
            <IconButton
              icon="trash"
              title="Delete point (Del)"
              size={13}
              onClick={() => {
                draft.remove(sel);
                setSelected(null);
              }}
            />
          </>
        ) : (
          <span className="muted curve-help" title={CANVAS_HELP}>
            {points.length ? 'Select a point to edit it numerically.' : 'Click in a view to place the first point.'}
          </span>
        )}
      </div>
      {sel !== null && renderPoint && <div className="curve-point-extra">{renderPoint(sel)}</div>}
    </div>
  );
}
